<template>
  <div class="headlines">
    <page-header title="Possible Headlines">
      <template #actions>
        <btn
          :disabled="selectedHeadlines.length < 1"
          :loading="submitting"
          loading-text="Submitting"
          @click="submit"
        >
          Submit
        </btn>
      </template>
    </page-header>

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="rowing"
        title="There are no headline sales yet"
      />

      <div v-if="!showEmptyState" class="selected-headlines">
        <div><b>{{ selectedHeadlines.length }}</b> selected</div>
        <div v-for="headline in selectedHeadlines" :key="headline.id">
          {{ headline.cardLabel }} <b>{{ $filters.currency(headline.price, { full: true }) }}</b>
        </div>
      </div>

      <template>
        <section class="headlines-list">
          <div
            v-for="headline in headlines"
            :key="headline.id"
            :class="['headline', { selected: Boolean(selectedHeadlines.find((selectedHeadline) => selectedHeadline.id === headline.id)) }]"
            @click="toggleSelectedHeadline(headline)"
          >
            <align flex class="header" direction="flex-start">
              <a class="link" :href="`/card/${headline.cardId}?showSales=true`" target="_blank">
                <div><b>{{ headline.cardLabel }}</b></div>
              </a>

              <external-link
                v-if="headline.platform === 'eBay'"
                :href="getTerapeakLink(headline)"
              >
                <span>Tera</span>
              </external-link>

              <icon-button
                icon="delete_outline"
                @click.stop.prevent="deletePossibleHeadline(headline.id)"
              />
            </align>

            <align direction="flex-start">
              <div class="images-container">
                <card-image :image-url="headline.image" />
              </div>

              <div class="details-container">
                <a
                  v-if="headline.title"
                  :href="headline.url"
                  class="link title"
                  target="_blank"
                >
                  <b>{{ headline.title }}</b>
                </a>

                <div>
                  <span>Price:</span> <b>{{ $filters.currency(headline.price, { full: true }) }}</b>
                </div>

                <div>
                  <span>End Date:</span> <b>{{ $filters.date(headline.date) }}</b>
                </div>

                <div v-if="headline.listingType">
                  <span>Type:</span> <b>{{ headline.listingType }}</b>
                </div>

                <div v-if="headline.employee">
                  <span>Employee:</span> <b>{{ employeeOptions.find(({ value }) => value === headline.employee)?.label }}</b>
                </div>

                <div v-if="headline.platform">
                  <span>Platform:</span> <b>{{ headline.platform }}</b>
                </div>

                <div v-if="headline.allTimeHigh" class="success">
                  All-time high
                </div>
              </div>
            </align>
          </div>
        </section>
      </template>
    </div>

    <scroll-top />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import moment from 'moment';
import * as Sentry from '@sentry/vue';

import { cardService, saleService } from '@/services';
import { getGeneratedAllTimeHighNote } from '@cardladder/shared/utils/feed.util';
import { toImageUrl, getTerapeakLink } from '@cardladder/shared/utils/format.util';

import CardImage from '@cardladder/shared/components/CardImage';

export default {
  components: {
    CardImage,
  },

  data() {
    return {
      loading: false,
      loaded: false,
      submitting: false,
      headlines: [],
      selectedHeadlines: [],
    };
  },

  created() {
    this.load();
  },

  computed: {
    ...mapState({
      employeeOptions: (state) => state.employeeOptions,
    }),

    showEmptyState() {
      return !this.loading && this.loaded && this.headlines.length === 0;
    },
  },

  methods: {
    async load() {
      try {
        this.loading = true;

        const response = await saleService.getPossibleHeadlines();

        this.headlines = response.map((headline) => ({
          ...headline,
          image: toImageUrl(`cards/thumb_${headline.cardId}`),
        }));
      } catch (e) {
        Sentry.captureException(e);
      }

      this.loaded = true;
      this.loading = false;
    },

    async submit() {
      this.submitting = true;

      const post = {
        id: null,
        image: null,
        title: `Ladder Headlines: ${moment().format('MM/DD/YYYY')}`,
        link: '',
        category: 'Headline',
        body: '',
        cardNotes: [],
      };

      for (let i = 0; i < this.selectedHeadlines.length; i += 1) {
        const selectedHeadline = this.selectedHeadlines[i];
        const cardNote = await getGeneratedAllTimeHighNote(selectedHeadline.cardId, cardService, saleService, i);

        post.cardNotes.push(cardNote);
      }

      this.$bus.$emit('admin-create-post', post);
      this.submitting = false;
    },

    async deletePossibleHeadline(id) {
      try {
        this.headlines = this.headlines.filter(({ id: headlineId }) => headlineId !== id);
        this.selectedHeadlines = this.selectedHeadlines.filter((headline) => headline.id !== id);
        await saleService.deletePossibleHeadline(id);
        this.$toasted.success('Possible headline deleted');
      } catch (e) {
        this.$toasted.error('Error deleting the possible headline');
      }
    },

    toggleSelectedHeadline(selectedHeadline) {
      const selectedHeadlineIndex = this.selectedHeadlines.findIndex((headline) => headline.id === selectedHeadline.id);

      if (selectedHeadlineIndex > -1) {
        this.selectedHeadlines.splice(selectedHeadlineIndex, 1);
      } else {
        this.selectedHeadlines.push(selectedHeadline);
      }
    },

    getTerapeakLink(headline) {
      return getTerapeakLink(headline.title, headline.isInternationalListing);
    },
  },
};
</script>

<style lang="scss" scoped>
.headlines {
  @include container;
  max-width: none;
  min-height: 100%;
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.header {
  margin-bottom: $gp;
}

.headlines-list {
  display: grid;
  grid-gap: $gp * 2;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media($xlarge-up) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media($large-down) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.headline {
  padding: $gp;
  background-color: var(--theme-card);
  box-shadow: $elevation-1;
  border-radius: $border-radius;
  display: flex;
  flex-direction: column;
  border: 2px solid var(--theme-card);

  @media($medium-down) {
    border-radius: 0;
  }

  &:hover,
  &.selected {
    cursor: pointer;
    background-color: var(--theme-active);
  }

  &.selected {
    border-color: $color-green-1;
  }
}

.images-container {
  min-width: 120px;
  max-width: 160px;
  margin-right: $gp;
}

.platform-image {
  min-height: 100px;
  width: auto;
}

.details-container {
  flex: 1;
}

.selected-headlines {
  margin-bottom: $gp;
}
</style>
